import React, { useCallback, useEffect, useState } from "react";

import { useHistory } from "react-router-dom";

import RegisterDeveloperCompany from "../../../../../presentation/pages/register-account/register-developer-company";
import NfseNacionalCard from "../../../../../presentation/components/nfse-nacional-card";

import { makeRemoteStack } from "../../../usecases/stack/remote-stack-factory";

import { api } from "../../../../../infra/http/remote";

import {
  taxTypeList,
  especialTaxTypeList,
  addressTypeList,
} from "../../../../../utils";
import { getUserRegisterInfo } from "./utils";

import {
  handleCepChange,
  handleNoStackChange,
  makeDeveloper,
  makeCompany,
  saveDataToLocalStorage,
  showSuccessMessage,
  showErrorMessage,
  getErrorMessage,
  handleCnpjChange,
} from "../../../../../presentation/helpers";
import { applyNamedMask } from "../../../../../utils/mask";

const isMobile = window.innerWidth < 768;

const MakeRegisterDeveloperCompany: React.FC = () => {
  const { userEmail, registerData } = getUserRegisterInfo();

  const history = useHistory();

  const [errorMessage] = useState<string | null>("");

  const [stacks, setStacks] = useState<any[]>([]);

  useEffect(() => {
    const fetchStacks = async (): Promise<void> => {
      const response = await makeRemoteStack().loadAll();
      setStacks(response);
    };
    fetchStacks();
  }, []);

  const createRequestBody = (data: any): any => ({
    ...makeDeveloper(data),
    company: makeCompany(data),
    stacks: data.stacks.map((stack: any) => ({ id: stack })),
  });

  const submitDeveloperCompany = async (data: any): Promise<void> => {
    await api.post("/public/register/developer-company/step-one", data);
  };

  const redirect = (path: string): void => {
    history.push(path);
  };

  const handleSubmit = async (data: any): Promise<void> => {
    const requestBody = createRequestBody(data);

    try {
      await submitDeveloperCompany(requestBody);
      saveDataToLocalStorage("registerData", requestBody);
      showSuccessMessage("Salvo com sucesso!");
      redirect("/cadastrar-empresa/2");
    } catch (error: any) {
      showErrorMessage(getErrorMessage(error));
    }
  };

  const goBack = (): void => {
    history.goBack();
  };

  const NfseNacionalComponent = useCallback(
    () => (
      <NfseNacionalCard
        mode="array"
        initialChecked={registerData?.company?.nfseNacional}
      />
    ),
    [registerData?.company?.nfseNacional],
  );

  const dataForm = [
    {
      items: [
        {
          name: "title",
          component: "Title",
          title: "Dados pessoais: ",
          number: 1,
        },
        {
          component: "InputGroup",
          style: {
            display: isMobile ? "flex" : "grid",
            gridTemplateColumns: "2fr 1fr",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 0 : "16px",
          },
          inputs: [
            {
              name: "name",
              component: "Input",
              value: registerData?.name || "",
              title: "Nome",
              label: "Nome",
              placeholder: "Ex. João da Silva",
              required: true,
              validations: "inputRequired",
            },
            {
              component: "InputMask",
              mask: "CPF",
              name: "cpf",
              label: "CPF",
              placeholder: "00.000.000-00",
              title: "CPF",
              value: registerData?.cpf || "",
              required: true,
              validations: "cpfValidation",
            },
          ],
        },
        {
          component: "InputGroup",
          style: {
            display: isMobile ? "flex" : "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 0 : "16px",
          },
          inputs: [
            {
              name: "email",
              component: "Input",
              value: userEmail || "",
              title: "E-mail",
              label: "E-mail",
              disabled: Boolean(registerData?.email),
              required: true,
              validations: "emailRequired",
            },
            {
              component: "InputMask",
              mask: "MOBILE_NUMBER",
              name: "phone",
              placeholder: "(00) 00000-0000",
              label: "Telefone",
              title: "Telefone",
              value:
                (registerData?.phone?.ddd &&
                  registerData?.phone?.number &&
                  `${registerData?.phone?.ddd}${registerData?.phone?.number}`) ||
                "",
              required: true,
              validations: "phoneNumberRequired",
            },
          ],
        },
        {
          component: "InputGroup",
          style: {
            display: isMobile ? "flex" : "grid",
            gridTemplateColumns: "2fr 1fr",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 0 : "16px",
          },
          hasDivider: true,
          inputs: [
            {
              name: "stacks",
              title: "Stacks",
              component: "Multiselect",
              styleContainer: { margin: "0px", marginTop: isMobile && "10px" },
              initialSelectedOptions:
                registerData?.stacks?.map((stack: any) => stack.id) || [],
              options: stacks
                .filter((stack) => stack.description !== "Não desejo informar")
                .map((stack) => ({
                  label: stack.description,
                  value: stack.id,
                })),
            },
            {
              component: "InputGroup",
              style: {
                display: "flex",
                flexDirection: "column",
                marginTop: isMobile && "20px",
                width: "100%",
              },
              inputs: [
                {
                  name: "otherStack",
                  component: "MultipleTextInput",
                  styleContainer: {
                    margin: "0px",
                  },
                  value: registerData?.otherStack || "",
                  title: "Outra stack",
                  label: "Outra stack",
                },
                {
                  name: "noStack",
                  component: "InputCheck",
                  title: "",
                  label: "",
                  onChange: (value: any, el: any) =>
                    handleNoStackChange(value, el, stacks),
                  checked:
                    registerData?.stacks?.some(
                      (stack: any) =>
                        stack.description === "Não desejo informar",
                    ) || false,
                  options: [
                    {
                      value: "true",
                      label: "Não desejo informar",
                      name: "Não desejo informar",
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          name: "title",
          component: "Title",
          title: "Endereço Residencial: ",
          number: 2,
        },
        {
          component: "InputGroup",
          style: {
            display: isMobile ? "flex" : "grid",
            gridTemplateColumns: "1fr 2fr",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 0 : "16px",
          },
          inputs: [
            {
              name: "developerCep",
              component: "InputMask",
              mask: "CEP",
              placeholder: "00000-000",
              label: "CEP",
              title: "CEP",
              value: registerData?.address?.zipCode || "",
              required: true,
              validations: "inputRequired",
              onChange: (data: any, el: any) =>
                handleCepChange(data, el, "developer"),
            },
            {
              component: "Input",
              name: "developerStreet",
              label: "Rua",
              placeholder: "Ex. Rua das Flores",
              title: "Rua",
              value: registerData?.address?.place || "",
              required: true,
              validations: "inputRequired",
            },
          ],
        },
        {
          component: "InputGroup",
          style: {
            display: isMobile ? "flex" : "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 0 : "16px",
          },
          inputs: [
            {
              name: "developerNumber",
              component: "Input",
              label: "Número",
              title: "Número",
              placeholder: "Ex. 123",
              value: registerData?.address?.number || "",
              required: true,
              validations: "inputRequired",
            },
            {
              name: "developerNeighborhood",
              component: "Input",
              label: "Bairro",
              title: "Bairro",
              placeholder: "Ex. Jardim das Flores",
              value: registerData?.address?.district || "",
              required: true,
              validations: "inputRequired",
            },
            {
              name: "developerComplement",
              component: "Input",
              label: "Complemento",
              placeholder: "Ex. Casa 2",
              title: "Complemento",
              value: "",
              required: false,
            },
          ],
        },
        {
          component: "InputGroup",
          style: {
            display: isMobile ? "flex" : "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 0 : "16px",
          },
          hasDivider: true,
          inputs: [
            {
              name: "developerCity",
              component: "Input",
              label: "Cidade",
              title: "Cidade",
              placeholder: "Ex. São Paulo",
              value: registerData?.address?.city || "",
              required: true,
              validations: "inputRequired",
            },
            {
              name: "developerState",
              component: "Input",
              label: "Estado",
              title: "Estado",
              placeholder: "Ex. SP",
              value: registerData?.address?.state || "",
              required: true,
              validations: "inputRequired",
            },
          ],
        },
        {
          name: "title",
          component: "Title",
          title: "Dados CNPJ: ",
          number: 3,
        },
        {
          component: "InputGroup",
          style: {
            display: isMobile ? "flex" : "grid",
            gridTemplateColumns: "2fr 1fr",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 0 : "16px",
          },
          inputs: [
            {
              name: "socialReason",
              component: "Input",
              label: "Razão Social",
              placeholder: "Ex. Empresa LTDA",
              title: "Razão Social",
              value: registerData?.company?.socialReason || "",
              required: true,
              validations: "inputRequired",
            },
            {
              component: "InputMask",
              mask: "CNPJ",
              name: "cnpj",
              label: "CNPJ",
              placeholder: "00.000.000/0000-00",
              title: "CNPJ",
              value: registerData?.company?.subscriptionNumber || "",
              onChange: (data: any, el: any) => {
                handleCnpjChange(data, el);
              },
              required: true,
              validations: "cpnjValidation",
            },
          ],
        },
        {
          component: "InputGroup",
          style: {
            display: isMobile ? "flex" : "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            alignItems: "center",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 0 : "16px",
          },
          inputs: [
            {
              name: "stateRegistration",
              component: "Input",
              label: "Inscrição Estadual",
              placeholder: "Ex. 123456789",
              title: "Inscrição Estadual",
              value: registerData?.company?.stateRegistration || "",
            },
            {
              name: "municipalRegistration",
              component: "Input",
              label: "Inscrição Municipal",
              title: "Inscrição Municipal",
              placeholder: "Ex. 123456789",
              value: registerData?.company?.municipalRegistration || "",
              required: true,
              validations: "inputRequired",
            },
            {
              name: "isMei",
              component: "InputCheck",
              title: "",
              label: "",
              checked: registerData?.company?.isMei || false,
              options: [
                {
                  value: "true",
                  label: "O CNPJ é MEI?",
                  name: "O CNPJ é MEI?",
                },
              ],
            },
          ],
        },
        {
          component: "InputGroup",
          style: {
            display: "flex",
            width: "100%",
          },
          inputs: [
            {
              name: "companyActivities",
              component: "ActivitiesList",
              label: "Atividades Econômicas (CNAE)",
              title: "Atividades Econômicas (CNAE)",
              value: [],
            },
          ],
        },
        {
          component: "InputGroup",
          style: {
            display: isMobile ? "flex" : "grid",
            gridTemplateColumns: "2fr 1fr",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 0 : "16px",
          },
          inputs: [
            {
              name: "fantasyName",
              component: "Input",
              label: "Nome Fantasia",
              placeholder: "Ex. Empresa LTDA",
              title: "Nome Fantasia",
              value: registerData?.company?.fantasyName || "",
              required: true,
              validations: "inputRequired",
            },
            {
              component: "Input",
              name: "companyEmail",
              label: "E-mail Cadastrado",
              placeholder: "company@email.com",
              title: "E-mail Cadastrado",
              value: registerData?.company?.email || "",
              required: true,
              validations: "emailRequired",
            },
          ],
        },
        {
          component: "InputGroup",
          style: {
            display: isMobile ? "flex" : "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 0 : "16px",
          },
          inputs: [
            {
              name: "companyPhone",
              component: "InputMask",
              mask: "PHONE_NUMBER",
              label: "Telefone",
              title: "Telefone",
              placeholder: "(00) 0000-0000",
              value: applyNamedMask("PHONE_NUMBER", (registerData?.company?.phone?.ddd &&
                registerData?.company?.phone?.number) ?
                `${registerData?.company?.phone?.ddd}${registerData?.company?.phone?.number}` :
                "")
              ,
              required: true,
              validations: "phoneNumberRequired",
            },
            {
              component: "InputMask",
              mask: "MOBILE_NUMBER",
              name: "companyMobilePhone",
              placeholder: "(00) 00000-0000",
              label: "Celular",
              title: "Celular",
            },
            {
              name: "taxType",
              component: "Select",
              label: "Regime Tributário",
              title: "Regime Tributário",
              value: registerData?.company?.taxType || "",
              required: true,
              validations: "inputRequired",
              options: taxTypeList.map((tax) => ({
                value: tax.ENUM,
                label: tax.label,
              })),
            },
            {
              name: "especialTaxType",
              component: "Select",
              label: "Regime Tributário Especial",
              title: "Regime Tributário Especial",
              value: registerData?.company?.especialTaxType || especialTaxTypeList.find((item) => item.value === 9)?.ENUM,
              required: true,
              validations: "inputRequired",
              options: especialTaxTypeList.map((tax) => ({
                value: tax.ENUM,
                label: tax.label,
              })),
            },
          ],
        },
        {
          component: "InputGroup",
          hasDivider: true,
          style: {
            display: isMobile ? "flex" : "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 0 : "16px",
          },
          inputs: [
            {
              name: "simplesNacional",
              component: "InputCheck",
              title: "",
              label: "",
              checked: registerData?.company?.simpleNacional || false,
              options: [
                {
                  value: "true",
                  label: "Optante pelo Simples Nacional",
                  name: "Optante pelo Simples Nacional",
                },
              ],
            },
            {
              name: "culturalIncentive",
              component: "InputCheck",
              title: "",
              label: "",
              checked: registerData?.company?.culturalIncentive || false,
              options: [
                {
                  value: "true",
                  label: "Incentivo a Cultura",
                  name: "Incentivo a Cultura",
                },
              ],
            },
            {
              name: "taxIncentive",
              component: "InputCheck",
              title: "",
              label: "",
              checked: registerData?.company?.taxIncentive || false,
              options: [
                {
                  value: "true",
                  label: "Taxa de incentivo",
                  name: "Taxa de incentivo",
                },
              ],
            },
          ],
        },
        {
          name: "title",
          component: "Title",
          title: "Nota Fiscal Nacional: ",
          number: 4,
        },
        {
          component: "InputGroup",
          hasDivider: true,
          style: {
            display: isMobile ? "flex" : "grid",
            gridTemplateColumns: "1fr",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 0 : "16px",
          },
          inputs: [
            {
              name: "nfseNacional",
              component: NfseNacionalComponent,
            },
          ],
        },
        {
          name: "title",
          component: "Title",
          title: "Endereço do CNPJ: ",
          number: 5,
        },
        {
          component: "InputGroup",
          style: {
            display: isMobile ? "flex" : "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 0 : "16px",
          },
          inputs: [
            {
              name: "companyCep",
              component: "InputMask",
              mask: "CEP",
              label: "CEP",
              placeholder: "00000-000",
              title: "CEP",
              value: registerData?.company?.address?.zipCode || "",
              required: true,
              validations: "inputRequired",
              onChange: (data: any, el: any) =>
                handleCepChange(data, el, "company"),
            },
            {
              name: "tipoLogradouro",
              component: "Select",
              label: "Tipo de Logradouro",
              title: "Tipo de Logradouro",
              value: registerData?.company?.address?.tipoLogradouro || "",
              required: true,
              validations: "inputRequired",
              options: addressTypeList.map((address) => ({
                value: address.ENUM,
                label: address.label,
              })),
            },
            {
              component: "Input",
              name: "companyStreet",
              label: "Rua",
              placeholder: "Ex. Rua das Flores",
              title: "Rua",
              value: registerData?.company?.address?.place || "",
              required: true,
              validations: "inputRequired",
            },
          ],
        },
        {
          component: "InputGroup",
          style: {
            display: isMobile ? "flex" : "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 0 : "16px",
          },
          inputs: [
            {
              name: "companyNumber",
              component: "Input",
              label: "Número",
              placeholder: "Ex. 123",
              title: "Número",
              value: registerData?.company?.address?.number || "",
              required: true,
              validations: "inputRequired",
            },
            {
              name: "companyNeighborhood",
              component: "Input",
              label: "Bairro",
              placeholder: "Ex. Jardim das Flores",
              title: "Bairro",
              value: registerData?.company?.address?.district || "",
              required: true,
              validations: "inputRequired",
            },
            {
              name: "companyComplement",
              component: "Input",
              label: "Complemento",
              placeholder: "Ex. Casa 2",
              title: "Complemento",
              value: registerData?.company?.address?.complement || "",
              required: false,
            },
          ],
        },
        {
          component: "InputGroup",
          style: {
            display: isMobile ? "flex" : "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 0 : "16px",
          },
          hasDivider: true,
          inputs: [
            {
              name: "companyCity",
              component: "Input",
              label: "Cidade",
              placeholder: "Ex. São Paulo",
              title: "Cidade",
              value: registerData?.company?.address?.city || "",
              required: true,
              validations: "inputRequired",
            },
            {
              name: "companyState",
              component: "Input",
              label: "Estado",
              placeholder: "Ex. SP",
              title: "Estado",
              value: registerData?.company?.address?.state || "",
              required: true,
              validations: "inputRequired",
            },
          ],
        },
        {
          component: "InputGroup",
          style: {
            display: "flex",
            gap: "16px",
            width: "100%",
            margin: "auto",
            marginTop: "16px",
          },
          inputs: [
            {
              name: "backButton",
              component: "Button",
              type: "button",
              title: "Voltar",
              onClick: goBack,
              color: false,
            },
            {
              name: "button",
              component: "Button",
              title: "Enviar",
              submittingText: "Carregando...",
              color: true,
            },
          ],
        },
      ],
    },
  ];

  const props = {
    dataForm,
    onSubmit: handleSubmit,
    errorMessage,
  };

  return <RegisterDeveloperCompany {...props} />;
};

export default MakeRegisterDeveloperCompany;
